import React, { memo, Suspense, useEffect } from "react";
import { Router, RouteComponentProps, navigate } from "@reach/router";
import Applications from "../Applications";
import { Header } from "../../sections";
import { Messages } from "../Messages";
import { useCommonWidths } from "../../../hooks";
import {
	headerHeightMobile,
	headerHeight,
	leftMenuWidth,
	basepath,
} from "../../../constants";
import { makeStyles } from "@material-ui/core";
import { Dashboard } from "../BackofficeApplications";
import { Consents } from "../Consents";
import {
	MessageResource,
	ApplicationsResource,
	ConsentResource,
	BackofficeApplicationResource,
	LoginResource,
} from "../../../resources";
import { ProgressIndicator } from "../../common";
import { useInvalidateOnUnmount } from "../../../utils";
import { useResource } from "rest-hooks";

interface MainSectionProps extends RouteComponentProps {}

const useStyles = makeStyles((theme) => ({
	fullMarginWrapper: {
		[theme.breakpoints.up("md")]: {
			marginLeft: leftMenuWidth,
			marginRight: leftMenuWidth,
		},
		[theme.breakpoints.down("md")]: {
			marginRight: 0,
		},
		[theme.breakpoints.down("sm")]: {
			marginRight: 0,
		},
	},
	lefMarginWrapper: {
		[theme.breakpoints.up("md")]: {
			marginLeft: leftMenuWidth,
		},
	},
}));

const MainSection = memo((props: MainSectionProps) => {
	const { uri: navigatedFrom } = props;

	const loginStatus = useResource(LoginResource.detailShape(), {});

	const isMobile = useCommonWidths().isSmDown;
	const classes = useStyles();

	const allMessages: MessageResource[] = useResource(
		MessageResource.listShape(),
		{}
	);
	const allApps = useResource(ApplicationsResource.listShape(), {});
	const backofficeApps = useResource(
		BackofficeApplicationResource.listShape(),
		{}
	);
	const consents = useResource(ConsentResource.listShape(), {});

	useInvalidateOnUnmount(ConsentResource.listShape(), {});

	const fallBackUrl = `${basepath}/logout`;
	useEffect(() => {
		if (
			!loginStatus.hasAdminAccess &&
			consents.find((c) => c.id === "ecommunications")?.consented !== true
		) {
			navigate(fallBackUrl);
		}
		if (
			allApps.length <= 0 &&
			backofficeApps.length > 0 &&
			navigatedFrom === "/" //Navigated to MainSection from Login, only then should we redirect
		) {
			const runningContracts = backofficeApps.filter(
				(contract) => !contract.fulfilled && !contract.toDebtCollection
			);
			const debtcollectionContracts = backofficeApps.filter(
				(contract) => !contract.fulfilled && contract.toDebtCollection
			);
			if (runningContracts.length <= 0) {
				if (debtcollectionContracts.length > 0) {
					navigate("/backofficeapplication/" + debtcollectionContracts[0].id);
				} else if (backofficeApps.length > 0) {
					navigate("/backofficeapplication/" + backofficeApps[0].id);
				}
			} else {
				navigate("/backofficeapplication/" + runningContracts[0].id);
			}
		}
	}, [allApps.length, backofficeApps, consents, fallBackUrl, navigatedFrom]);

	return (
		<>
			<Header
				authenticated={true}
				userHasUnreadMessages={allMessages.filter((m) => !m.opened).length > 0}
				userMatchesAdminPolicy={loginStatus.hasAdminAccess}
			/>
			<div style={{ marginTop: isMobile ? headerHeightMobile : headerHeight }}>
				<Router>
					<MainSectionWrapper default className={classes.fullMarginWrapper}>
						<Applications
							default
							path="/applications"
							allApps={allApps}
						></Applications>
						<Messages
							path="/messages"
							allMessages={allMessages.map((x) => x.toClientModel())}
						></Messages>
						<Consents path="/consents" />
					</MainSectionWrapper>
					<MainSectionWrapper
						className={classes.lefMarginWrapper}
						path="/backofficeapplication"
					>
						<Dashboard path="/:id" />
					</MainSectionWrapper>
				</Router>
			</div>
		</>
	);
});

interface MainSectionWrapperProps extends RouteComponentProps {
	children: React.ReactNode;
	className: string;
}

const MainSectionWrapper = (props: MainSectionWrapperProps) => {
	const { className, children } = props;
	return (
		<Suspense
			fallback={
				<ProgressIndicator type="circular" wrapperHeight={headerHeight} />
			}
		>
			<div className={className}>{children}</div>
		</Suspense>
	);
};

export default MainSection;
